import { TaskTypeUiSchema, TaskTypeUiStateStyle } from '../models/task-type.model';

/** Identifies whether a resolved schema came from persisted customization or a fallback palette. */
export type TaskTypeUiSchemaSource = 'custom' | 'color' | 'default';

/** Contains a render-ready schema and the fallback path that produced it. */
export interface ResolvedTaskTypeUiSchema {
  /** Render-ready visual schema. */
  schema: TaskTypeUiSchema;
  /** Source selected by the resolver. */
  source: TaskTypeUiSchemaSource;
}

const SAFE_DEFAULT_COLOR = '#6c757d';
const SCHEMA_KEYS = ['iconId', 'normal', 'hover', 'active'];
const STATE_KEYS = ['iconColor', 'outlineColor', 'backgroundColor', 'textColor'];

/** Determines whether a value is a supported three- or six-digit hexadecimal color. */
export function isTaskTypeHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim());
}

/**
 * Resolves Task Type presentation without mutating or persisting fallback values.
 *
 * @remarks
 * Resolution order is: a complete supported custom schema, a palette derived from
 * a valid base color, then a neutral palette. Partial schemas, malformed JSON and
 * unknown properties are ignored so every consumer receives the same safe result.
 *
 * @param uiSchema - Raw JSON stored on the Task Type, or an empty fallback marker.
 * @param colorCode - Base Task Type color used when no valid custom schema exists.
 * @returns A render-ready schema together with its selected source.
 */
export function resolveTaskTypeUiSchema(uiSchema: string | null | undefined, colorCode: string | null | undefined): ResolvedTaskTypeUiSchema {
  const custom = parseTaskTypeUiSchema(uiSchema);
  if (custom) {
    return { schema: custom, source: 'custom' };
  }

  if (isTaskTypeHexColor(colorCode)) {
    return { schema: deriveTaskTypeUiSchema(colorCode), source: 'color' };
  }

  return { schema: deriveTaskTypeUiSchema(SAFE_DEFAULT_COLOR), source: 'default' };
}

/**
 * Creates deterministic normal, hover and active states from a base color.
 *
 * @param colorCode - Valid hexadecimal base color.
 * @returns A contrast-aware visual schema whose active background preserves the base color.
 */
export function deriveTaskTypeUiSchema(colorCode: string): TaskTypeUiSchema {
  const base = normalizeHex(colorCode);
  const normalBackground = mixWithWhite(base, 0.88);
  const hoverBackground = mixWithWhite(base, 0.74);

  return {
    iconId: 'label',
    normal: createState(base, mixWithWhite(base, 0.55), normalBackground),
    hover: createState(base, mixWithWhite(base, 0.35), hoverBackground),
    active: createState(contrastColor(base), base, base)
  };
}

/** Serializes only fields accepted by the strict Task Type backend contract. */
export function serializeTaskTypeUiSchema(schema: TaskTypeUiSchema): string {
  return JSON.stringify({
    iconId: schema.iconId,
    normal: copyState(schema.normal),
    hover: copyState(schema.hover),
    active: copyState(schema.active)
  });
}

/** Parses only complete schemas accepted by the shared frontend contract. */
function parseTaskTypeUiSchema(value: string | null | undefined): TaskTypeUiSchema | null {
  if (!value || !value.trim()) {
    return null;
  }

  try {
    const candidate = JSON.parse(value);
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
      return null;
    }
    if (Object.keys(candidate).some(key => !SCHEMA_KEYS.includes(key))) {
      return null;
    }
    if (candidate.iconId !== undefined && typeof candidate.iconId !== 'string') {
      return null;
    }
    if (!isState(candidate.normal) || !isState(candidate.hover) || !isState(candidate.active)) {
      return null;
    }
    return {
      iconId: candidate.iconId,
      normal: copyState(candidate.normal),
      hover: copyState(candidate.hover),
      active: copyState(candidate.active)
    };
  } catch {
    return null;
  }
}

/** Determines whether a candidate contains exactly the four supported state colors. */
function isState(value: any): value is TaskTypeUiStateStyle {
  return !!value && typeof value === 'object' && !Array.isArray(value) &&
    Object.keys(value).length === STATE_KEYS.length &&
    Object.keys(value).every(key => STATE_KEYS.includes(key)) &&
    STATE_KEYS.every(key => isTaskTypeHexColor(value[key]));
}

function createState(foreground: string, outlineColor: string, backgroundColor: string): TaskTypeUiStateStyle {
  return {
    iconColor: foreground,
    outlineColor,
    backgroundColor,
    textColor: foreground
  };
}

function copyState(state: TaskTypeUiStateStyle): TaskTypeUiStateStyle {
  return {
    iconColor: state.iconColor,
    outlineColor: state.outlineColor,
    backgroundColor: state.backgroundColor,
    textColor: state.textColor
  };
}

function normalizeHex(color: string): string {
  const value = color.trim().toLowerCase();
  if (value.length === 4) {
    return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`;
  }
  return value;
}

/** Produces a stable tint while retaining the base hue for fallback states. */
function mixWithWhite(color: string, whiteRatio: number): string {
  const rgb = hexToRgb(color);
  return rgbToHex(
    Math.round(rgb.r + (255 - rgb.r) * whiteRatio),
    Math.round(rgb.g + (255 - rgb.g) * whiteRatio),
    Math.round(rgb.b + (255 - rgb.b) * whiteRatio)
  );
}

/** Chooses a high-contrast foreground for a hexadecimal background. */
function contrastColor(background: string): string {
  const rgb = hexToRgb(background);
  const channels = [rgb.r, rgb.g, rgb.b].map(channel => {
    const normalized = channel / 255;
    return normalized <= 0.03928 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4);
  });
  const luminance = channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  return luminance > 0.45 ? '#202124' : '#ffffff';
}

function hexToRgb(color: string): { r: number; g: number; b: number } {
  const normalized = normalizeHex(color).slice(1);
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16)
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map(value => value.toString(16).padStart(2, '0')).join('')}`;
}
